import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import type { TagProps } from "primereact/tag";
import type { IconOptions } from "primereact/utils";
import type { ReactNode } from "react";

import {
  actionTooltipProps,
  ErrorState,
  ExportTableDialog,
  HubTable,
  Icon,
  TableSettingsDialog,
} from "@/ds";
import type { UsersSortField } from "@/modules/users";
import { hubTableCartao, HubTableCartao } from "@/shared/components/hub-table-cartao";
import { ListaToolbar } from "@/shared/components/lista-toolbar";
import { UserAvatarByEmail } from "@/shared/components/user-avatar-by-email";
import { UserInfoCard } from "@/shared/components/user-info-card";
import { FiltrosInline } from "../components/filtros-inline";
import { EditUserDialog } from "./components/edit-user-dialog";
import { ToggleUserStatusDialog } from "./components/toggle-user-status-dialog";
import {
  PERFIL_FILTER_KEY,
  TIME_FILTER_KEY,
  USERS_COLUMN_LABELS,
  USERS_ERROR_MESSAGE,
  USERS_EXPORT_LIST_NAME,
  USERS_LOADING_LABEL,
  USERS_MAIN_COLUMNS,
  USERS_ROWS_PER_PAGE_LABEL,
  USERS_SEARCH_PLACEHOLDER,
} from "./users.constants";
import type { UsersColumnKey } from "./users.constants";
import styles from "./users.module.scss";
import type { UserLinha, UsersColuna, UsersViewModel } from "./types-users";

const EDIT_TOOLTIP = actionTooltipProps("Editar", { event: "hover" });

/** Ícone de 10,5 com a classe do Prime (`p-tag-icon`): o tema põe os 4px de `tag/gap` até o texto. */
function renderIconeAlerta({ iconProps }: IconOptions<TagProps>) {
  return <Icon name="exclamation-triangle" size={10.5} className={iconProps.className} />;
}

/** Badge da RN004, `tag` warn do DS (Figma 4966:62732 na linha e 4971:11200 no cabeçalho): triângulo, texto ou quantidade. */
function renderAlerta(value: string, testId?: string) {
  return <Tag severity="warning" icon={renderIconeAlerta} value={value} data-testid={testId} />;
}

/** Aba Usuários (Figma 4434:111149): busca, Time, Perfil e ⋮ sobre a tabela; o lápis ou a linha abre a edição de perfil. */
export function UsersView({
  isMobile,
  search,
  buscar,
  filterColumns,
  filterValue,
  applyFilters,
  settings,
  exportView,
  notifySettingsApplied,
  colunas,
  items,
  total,
  isFirstLoad,
  status,
  errorMessage,
  reload,
  table,
  dataTableProps,
  hasSearch,
  hasFilters,
  onClear,
  perfis,
  editando,
  editar,
  fecharEdicao,
  salvarEdicao,
  alternando,
  alternar,
  fecharAlternancia,
  confirmarAlternancia,
}: UsersViewModel) {
  function renderHeader({ header, selo }: UsersColuna): ReactNode {
    if (!selo) return header;

    return (
      <span className={styles.perfilHeader}>
        {header}
        <span title={selo.titulo} data-testid={selo.testId}>
          {renderAlerta(selo.texto)}
        </span>
      </span>
    );
  }

  function renderCell(key: UsersColumnKey, linha: UserLinha): ReactNode {
    switch (key) {
      case "foto":
        return (
          <UserInfoCard
            nome={linha.nome}
            email={linha.user.email}
            telefone={linha.user.celular}
            detalhe={linha.user.time}
            fotoUrl={linha.user.fotoUrl}
          >
            <UserAvatarByEmail
              nome={linha.nome}
              email={linha.user.email}
              fotoUrl={linha.user.fotoUrl}
            />
          </UserInfoCard>
        );
      case "papel":
        return linha.semPerfil ? renderAlerta(linha.perfil, linha.noRoleTestId) : linha.perfil;
      case "ultimaAtividade":
        return (
          <span className={styles.ultimaAtividade}>
            <span>{linha.ultimaAtividade.data}</span>
            {linha.ultimaAtividade.hora ? <span>{linha.ultimaAtividade.hora}</span> : null}
          </span>
        );
      case "status":
        return <span data-testid={linha.statusTestId}>{linha.status}</span>;
      case "acoes":
        return (
          <div className={styles.acoes}>
            <Button
              type="button"
              className={hubTableCartao.actionButtonClassName}
              icon={<Icon name="pencil" size={14} />}
              aria-label={linha.editLabel}
              aria-haspopup="dialog"
              tooltip={EDIT_TOOLTIP.content as string}
              tooltipOptions={{ position: EDIT_TOOLTIP.position, event: "hover" }}
              onClick={() => editar(linha)}
              data-testid={linha.editTestId}
              severity="secondary"
              text
            />
            <Button
              type="button"
              className={hubTableCartao.actionButtonClassName}
              icon={<Icon name="power-off" size={14} />}
              aria-label={linha.toggleLabel}
              aria-haspopup="dialog"
              tooltip={linha.toggleTooltip}
              tooltipOptions={{ position: EDIT_TOOLTIP.position, event: "hover" }}
              onClick={() => alternar(linha)}
              data-testid={linha.toggleTestId}
              severity="secondary"
              text
            />
          </div>
        );
      default:
        return linha[key];
    }
  }

  return (
    <section className={styles.container} data-testid="users-screen">
      <ListaToolbar
        testIdPrefix="users"
        isMobile={isMobile}
        search={search}
        onSearchChange={buscar}
        searchPlaceholder={USERS_SEARCH_PLACEHOLDER}
        searchWidth="13.625rem"
        inlineActions
        quickFilters={
          <FiltrosInline
            definitions={filterColumns}
            value={filterValue}
            onChange={applyFilters}
            testIdPrefix="users"
            classNames={{
              [TIME_FILTER_KEY]: styles.filtroTime,
              [PERFIL_FILTER_KEY]: styles.filtroPerfil,
            }}
          />
        }
        onExport={exportView.open}
        onOpenSettings={settings.dialog.open}
      />

      {exportView.isOpen ? (
        <ExportTableDialog
          listName={USERS_EXPORT_LIST_NAME}
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      <TableSettingsDialog<UsersColumnKey>
        dialog={settings.dialog}
        columnLabels={USERS_COLUMN_LABELS}
        mainColumns={USERS_MAIN_COLUMNS}
        showColumnsSection={settings.showColumnsSection}
        testId="users-table-settings"
        onApplied={notifySettingsApplied}
      />

      {alternando ? (
        // A chave descarta o estado de envio ao trocar de usuário.
        <ToggleUserStatusDialog
          key={alternando.id}
          user={alternando}
          onConfirm={() => confirmarAlternancia(alternando)}
          onCancel={fecharAlternancia}
        />
      ) : null}

      {editando ? (
        // A chave descarta o formulário ao trocar de usuário.
        <EditUserDialog
          key={editando.id}
          user={editando}
          roles={perfis}
          onSubmit={(values) => salvarEdicao(editando, values)}
          onCancel={fecharEdicao}
        />
      ) : null}

      <div className={styles.content}>
        <HubTableCartao<UserLinha, UsersColumnKey, UsersSortField>
          value={items}
          totalRecords={total}
          table={table}
          settings={settings}
          status={status}
          errorMessage={errorMessage}
          loadingLabel={USERS_LOADING_LABEL}
          onRowClick={editar}
          emptyNoun="usuário"
          emptyGender="m"
          hasSearch={hasSearch}
          hasFilters={hasFilters}
          onClear={onClear}
          dataTableProps={dataTableProps}
          rowsPerPageLabel={USERS_ROWS_PER_PAGE_LABEL}
          testId="users"
          data-testid="users-table-wrapper"
          data-first-load={isFirstLoad || undefined}
        >
          {colunas.map((coluna) => (
            <HubTable.Column<UserLinha, UsersColumnKey>
              key={coluna.key}
              field={coluna.key}
              columnKey={coluna.key}
              header={renderHeader(coluna)}
              sortable={coluna.sortable}
              align={coluna.align}
              alignHeader={coluna.align}
              bodyClassName={coluna.className}
              headerClassName={coluna.className}
              style={{ width: coluna.width, minWidth: coluna.width }}
              body={(linha) => renderCell(coluna.key, linha)}
            />
          ))}
          <HubTable.Error>
            <ErrorState
              message={USERS_ERROR_MESSAGE}
              description={errorMessage ?? undefined}
              onRetry={reload}
              testId="users-error"
            />
          </HubTable.Error>
        </HubTableCartao>
      </div>
    </section>
  );
}
